import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Divider,
  Button,
  Chip,
  Paper,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { useGetCurrentCompanyQuery } from '../../store/api/companyApi';
import { useGetActiveRegisterSessionQuery } from '../../store/api/salesApi';
import { useGetDashboardSummaryQuery } from '../../store/api/dashboardApi';
import { GlobalSearchDialog } from './GlobalSearchDialog';

export function TopNavigationBar({ onMobileMenuToggle, menuItems, isActive, onNavigate }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // APIs
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: activeSessionData } = useGetActiveRegisterSessionQuery(undefined, {
    skip: !company?.enableSalesSession,
    pollingInterval: 30000,
  });
  const { data: dashboardData } = useGetDashboardSummaryQuery(undefined, {
    pollingInterval: 60000,
  });

  // State
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickCreateAnchor, setQuickCreateAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Global Keyboard Shortcuts (Cmd+K / Ctrl+K for search)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Notifications calculation
  const lowStockList = dashboardData?.lowStockItems || [];
  const lowStockCount = lowStockList.length;
  const pendingInvoicesCount = Number(dashboardData?.pendingInvoicesCount || 0);
  const totalNotifCount = lowStockCount + (pendingInvoicesCount > 0 ? 1 : 0);

  // Timezone label helper
  const timezoneLabel = useMemo(() => {
    const tz = company?.timezone || 'Asia/Dubai';
    if (tz.includes('Dubai') || tz.includes('Muscat')) return 'UAE (UTC+4)';
    if (tz.includes('Riyadh') || tz.includes('Qatar') || tz.includes('Kuwait')) return 'Gulf (UTC+3)';
    if (tz.includes('Kolkata')) return 'IST (UTC+5:30)';
    if (tz.includes('London')) return 'UK (UTC+0)';
    if (tz.includes('New_York')) return 'EST (UTC-5)';
    return tz.split('/')[1] || tz;
  }, [company?.timezone]);

  const formattedTime = useMemo(() => {
    return currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, [currentTime]);

  const hasActiveShift = !!(
    activeSessionData?.id ||
    activeSessionData?.session ||
    (activeSessionData && typeof activeSessionData === 'object' && activeSessionData.status === 'open')
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.96)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
          color: '#1e293b',
          boxShadow: '0 4px 20px -4px rgba(0,0,0,0.05)',
          '@media print': { display: 'none' },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 1.5, sm: 2.5, md: 3 },
            py: 1,
            gap: { xs: 1, sm: 1.5, md: 2 },
            minHeight: { xs: 60, sm: 64 },
          }}
        >
          {/* ── LEFT: Logo & Company Capsule ──────────────────────── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {/* Mobile Hamburger */}
            <IconButton
              sx={{
                display: { xs: 'flex', lg: 'none' },
                color: '#475569',
                p: 0.75,
                bgcolor: '#f1f5f9',
                '&:hover': { bgcolor: '#e2e8f0' },
              }}
              onClick={onMobileMenuToggle}
            >
              <MenuIcon fontSize="small" />
            </IconButton>

            {/* Brand Logo */}
            <Box
              onClick={() => onNavigate('/dashboard')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.25,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              >
                <LocalFloristIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '1.05rem',
                    letterSpacing: '-0.02em',
                  }}
                >
                  YoroInventory
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  ERP & Accounting
                </Typography>
              </Box>
            </Box>

            {/* Company / Branch Pill */}
            {company && (
              <Tooltip title={`Company: ${company.name} • Currency: ${company.currency || 'USD'} • Timezone: ${company.timezone || 'Asia/Dubai'}`} arrow>
                <Chip
                  icon={<BusinessIcon sx={{ fontSize: 16, color: '#0ea5e9 !important' }} />}
                  label={company.name}
                  onClick={() => onNavigate('/settings')}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    height: 30,
                    borderRadius: 1,
                    bgcolor: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    color: '#0369a1',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    maxWidth: 180,
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    '&:hover': { bgcolor: '#e0f2fe' },
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* ── CENTER: Global Search Bar Trigger ─────────────────── */}
          <Box sx={{ flex: 1, maxWidth: { xs: 200, sm: 340, md: 420 }, mx: { xs: 0.5, sm: 1.5 } }}>
            <Paper
              elevation={0}
              onClick={() => setSearchOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.6,
                borderRadius: 1.25,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#ffffff',
                  borderColor: '#0ea5e9',
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Search invoices, items, customers...
                </Typography>
              </Box>
              <Chip
                label="⌘K"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  bgcolor: '#e2e8f0',
                  color: '#475569',
                  borderRadius: 1,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              />
            </Paper>
          </Box>

          {/* ── RIGHT: Widgets & User Controls ─────────────────────── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1, md: 1.25 } }}>
            {/* Live Clock & Timezone Widget */}
            <Box
              sx={{
                display: { xs: 'none', xl: 'flex' },
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 16, color: '#0ea5e9' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>
                {formattedTime}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b', bgcolor: '#e2e8f0', px: 0.6, py: 0.1, borderRadius: 1 }}>
                {timezoneLabel}
              </Typography>
            </Box>

            {/* POS Shift Indicator (if enabled) */}
            {company?.enableSalesSession && (
              <Tooltip title={hasActiveShift ? 'POS Cash Register Shift is OPEN. Click to open POS.' : 'POS Shift is CLOSED. Click to open a new register session.'} arrow>
                <Chip
                  onClick={() => onNavigate('/sales/orders/new')}
                  size="small"
                  label={hasActiveShift ? 'POS Active' : 'POS Closed'}
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    height: 28,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    bgcolor: hasActiveShift ? '#ecfdf5' : '#f1f5f9',
                    color: hasActiveShift ? '#059669' : '#64748b',
                    border: `1px solid ${hasActiveShift ? '#a7f3d0' : '#cbd5e1'}`,
                    '&:hover': { opacity: 0.9 },
                    '&::before': {
                      content: '""',
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: hasActiveShift ? '#10b981' : '#94a3b8',
                      mr: 0.75,
                    },
                  }}
                />
              </Tooltip>
            )}

            {/* Quick Create (+ New) Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => setQuickCreateAnchor(e.currentTarget)}
              sx={{
                height: 34,
                px: { xs: 1, sm: 1.5 },
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.4)',
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                New
              </Box>
            </Button>

            {/* Quick Create Menu */}
            <Menu
              anchorEl={quickCreateAnchor}
              open={Boolean(quickCreateAnchor)}
              onClose={() => setQuickCreateAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  borderRadius: 1.25,
                  mt: 1,
                  minWidth: 220,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #f1f5f9',
                  py: 0.5,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/sales/orders/new');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#0ea5e9' }}>
                  <PointOfSaleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>Sales Invoice</Typography>}
                  secondary={<Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>F2 Shortcut</Typography>}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/quotations/new');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#3b82f6' }}>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>Quotation</Typography>} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/purchases/new');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#10b981' }}>
                  <LocalShippingIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>Purchase Order (LPO)</Typography>} />
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/customers/new');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#a855f7' }}>
                  <PersonAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>New Customer</Typography>} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/products/new');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#f59e0b' }}>
                  <AddCircleOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>New Product</Typography>} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/finance/vouchers/new?type=payment');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#06b6d4' }}>
                  <ReceiptIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>Payment Voucher</Typography>} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQuickCreateAnchor(null);
                  onNavigate('/expenses');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 28, color: '#ef4444' }}>
                  <ReceiptLongIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontWeight: 600, fontSize: '0.86rem' }}>Record Expense</Typography>} />
              </MenuItem>
            </Menu>

            {/* Notification Bell */}
            <Tooltip title="Notifications & Low Stock Alerts" arrow>
              <IconButton
                onClick={(e) => setNotifAnchor(e.currentTarget)}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#f1f5f9', color: '#0ea5e9' },
                }}
              >
                <Badge
                  badgeContent={totalNotifCount}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: '#ef4444',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Notifications Dropdown */}
            <Menu
              anchorEl={notifAnchor}
              open={Boolean(notifAnchor)}
              onClose={() => setNotifAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  borderRadius: 1.25,
                  mt: 1,
                  width: 320,
                  maxHeight: 400,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #f1f5f9',
                  p: 0,
                },
              }}
            >
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>
                  Notifications ({totalNotifCount})
                </Typography>
                {lowStockCount > 0 && (
                  <Chip label={`${lowStockCount} Low Stock`} size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: '0.7rem' }} />
                )}
              </Box>

              {lowStockCount > 0 ? (
                <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                  {lowStockList.map((item, idx) => (
                    <MenuItem
                      key={item.id || idx}
                      onClick={() => {
                        setNotifAnchor(null);
                        onNavigate(`/products/${item.id}`);
                      }}
                      sx={{ py: 1, px: 1.5, borderBottom: '1px solid #f8fafc', gap: 1.25 }}
                    >
                      <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 600 }}>
                          Remaining: {item.quantity ?? 0} {item.unit || 'units'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center', color: '#64748b' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>All Good! No Alerts</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>Inventory levels are healthy.</Typography>
                </Box>
              )}

              <Box sx={{ p: 1, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
                <Button
                  size="small"
                  fullWidth
                  onClick={() => {
                    setNotifAnchor(null);
                    onNavigate('/inventory');
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600 }}
                >
                  View Full Inventory Report →
                </Button>
              </Box>
            </Menu>

            {/* User Profile Capsule */}
            <Box
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: 0.5,
                pr: 1.25,
                py: 0.5,
                borderRadius: 1.25,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#ffffff',
                  borderColor: '#0ea5e9',
                  boxShadow: '0 2px 8px rgba(14, 165, 233, 0.15)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: '#0ea5e9',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: 'white',
                  borderRadius: 1,
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || user?.name?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left', lineHeight: 1.1 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                  {user?.firstName || user?.name || 'Account'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>
                  {user?.isSuperAdmin ? 'Super Admin' : user?.role?.name || 'Staff'}
                </Typography>
              </Box>
              <ArrowDropDownIcon sx={{ fontSize: 16, color: '#94a3b8', display: { xs: 'none', sm: 'block' } }} />
            </Box>

            {/* User Dropdown Menu */}
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  borderRadius: 1.25,
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #f1f5f9',
                  py: 0.5,
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>
                  {user?.firstName ? `${user.firstName} ${user?.lastName || ''}` : user?.name || 'User'}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.email || 'Logged in'}
                </Typography>
              </Box>
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  onNavigate('/profile');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <AccountCircleIcon sx={{ fontSize: 18, color: '#0ea5e9' }} />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>My Profile</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  onNavigate('/settings');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <SettingsIcon sx={{ fontSize: 18, color: '#0ea5e9' }} />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>Company Settings</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  onNavigate('/settings/backup');
                }}
                sx={{ py: 1, px: 2, gap: 1.5 }}
              >
                <DescriptionIcon sx={{ fontSize: 18, color: '#0ea5e9' }} />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>Database Backup</Typography>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  onNavigate('/login');
                }}
                sx={{ py: 1, px: 2, gap: 1.5, color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600 }}>Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Global Search Dialog Modal (Cmd + K) */}
      <GlobalSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
